/**
 * Name FullCalendar's toolbar buttons from our side.
 *
 * FullCalendar renders its prev/next arrows as an EMPTY <span role="img">
 * inside the button and puts its own hint on the button's `title`. An empty
 * role="img" has no accessible text, which axe reports as `role-img-alt`
 * (serious), and a screen reader announces nothing useful.
 *
 * We keep the chevrons — this is third-party markup we do not want to restyle,
 * and switching to `buttonIcons: false` would replace them with text and change
 * the look. Instead the icon is marked decorative and the name is promoted onto
 * the button itself, where it belongs. FullCalendar rebuilds the toolbar on
 * every view and date change, so callers must run this again on `datesSet`.
 *
 * WHY THIS LIVES IN SiteAdmin/shared rather than SuperAdmin/shared, where it
 * started: four bundles need it, and two of them are the public marketing site
 * (BattleReady's signup wizard and contact form). `SiteAdmin/Resources/js/shared/`
 * is this repo's cross-module JS home — PointSale, SuperAdmin, Checkout and
 * BattleReady already import from it, and BattleReady's own script.js already
 * takes modal-zindex.js from here. `SuperAdmin/Resources/js/shared/` has no
 * cross-module consumer: its one importer outside the module is the agent
 * portal, which is composed from SuperAdmin's own bundles and views. Importing
 * the admin app into the marketing bundle would have been a boundary this repo
 * does not otherwise cross, so the module moved instead.
 *
 * ONE DELIBERATE DIFFERENCE from the original inline copy in demo-calendar.js
 * (Wave 3 a11y package, merged to staging as 79a7ac36b): the name is re-read
 * every time, where the inline copy only sets it when the button has no
 * aria-label yet. FullCalendar rewrites the hint per view — the same arrow is
 * "Previous week" in timeGridWeek and "Previous month" in dayGridMonth — so a
 * write-once label goes stale the moment the operator switches view, and a
 * screen reader then announces "Previous week" on a control that moves a month.
 * Confirmed in a browser on the demo calendar, which starts in month view and
 * offers week: after switching to week the button's `title` read "Previous
 * week" while its write-once `aria-label` still read "Previous month", and it
 * stayed wrong through every prev/next until the operator switched back.
 *
 * @param {Element|null} root The element FullCalendar was mounted on.
 */
export function nameToolbarControls(root) {
    if (!root) return;

    root.querySelectorAll('.fc-toolbar button').forEach((button) => {
        const hint = button.getAttribute('title');

        // The icon carries no text, so it must not be exposed at all.
        button.querySelectorAll('[role="img"]').forEach((icon) => {
            icon.setAttribute('aria-hidden', 'true');
            icon.removeAttribute('role');
        });

        // Buttons that already render their own text (today / week / month /
        // day / list) keep it; only the icon-only ones need a name supplied.
        const hasText = button.textContent.trim() !== '';
        if (!hasText && hint) {
            button.setAttribute('aria-label', hint);
        }
    });
}
